import { type ReactNode } from "react";
import Icon from "./Icon";

/* ── Card Skeleton ── */
interface SkeletonCardProps {
    /** Number of skeleton cards to render */
    count?: number;
    className?: string;
}

export function SkeletonCard({ count = 1, className = "" }: SkeletonCardProps): ReactNode {
    return (
        <>
            {Array.from({ length: count }).map((_, i) => (
                <div
                    key={i}
                    className={`rounded-[12px] p-[clamp(12px,1.7dvh,16px)] flex flex-col animate-pulse ${className}`}
                    style={{
                        background: "linear-gradient(to bottom, #171717, #131313)",
                        border: "1px solid rgba(153,153,153,0.3)",
                    }}
                >
                    {/* Header: avatar + ticker + badge */}
                    <div className="flex items-center gap-[clamp(8px,1.3dvh,12px)] mb-[clamp(8px,1.3dvh,12px)]">
                        <div className="size-[clamp(32px,4.3dvh,40px)] rounded-full bg-white/5 shrink-0" />
                        <div className="h-4 w-20 bg-white/5 rounded flex-1" />
                        <div className="h-6 w-14 bg-white/5 rounded-full" />
                    </div>
                    {/* Progress row */}
                    <div className="flex items-center justify-between mb-[6px]">
                        <div className="h-3.5 w-24 bg-white/5 rounded" />
                        <div className="h-3.5 w-10 bg-white/5 rounded" />
                    </div>
                    {/* Progress bar */}
                    <div className="h-[6px] w-full bg-white/10 rounded-full mb-[clamp(10px,1.7dvh,16px)]" />
                    {/* Stats: Members + Time Left */}
                    <div className="flex">
                        <div className="flex-1 space-y-[4px]">
                            <div className="h-2.5 w-14 bg-white/5 rounded" />
                            <div className="h-4 w-8 bg-white/5 rounded" />
                        </div>
                        <div className="flex-1 space-y-[4px]">
                            <div className="h-2.5 w-14 bg-white/5 rounded" />
                            <div className="h-4 w-12 bg-white/5 rounded" />
                        </div>
                    </div>
                </div>
            ))}
        </>
    );
}

/* ── Progress bar skeleton ── */
export function SkeletonProgress({ className = "" }: { className?: string }): ReactNode {
    return (
        <div className={`space-y-2 animate-pulse ${className}`}>
            <div className="flex justify-between">
                <div className="h-2.5 w-20 bg-white/5 rounded" />
                <div className="h-2.5 w-24 bg-white/5 rounded" />
            </div>
            <div className="w-full h-2 bg-white/5 rounded-full" />
        </div>
    );
}

/* ── Text line skeleton ── */
export function SkeletonLine({
    width = "w-full",
    height = "h-3",
    className = "",
}: {
    width?: string;
    height?: string;
    className?: string;
}): ReactNode {
    return <div className={`${width} ${height} bg-white/5 rounded animate-pulse ${className}`} />;
}

/* ── Circular skeleton ── */
export function SkeletonCircle({
    size = "w-10 h-10",
    className = "",
}: {
    size?: string;
    className?: string;
}): ReactNode {
    return <div className={`${size} rounded-full bg-white/5 animate-pulse ${className}`} />;
}

/* ── Empty State ── */
interface EmptyStateProps {
    /** SVG icon name (kebab-case) */
    icon?: string;
    title: string;
    description?: string;
    /** Optional CTA element */
    action?: ReactNode;
    className?: string;
}

export function EmptyState({
    icon = "inbox",
    title,
    description,
    action,
    className = "",
}: EmptyStateProps): ReactNode {
    return (
        <div className={`flex flex-col items-center justify-center py-16 px-6 gap-4 ${className}`}>
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
                <Icon name={icon} size={30} className="opacity-20" />
            </div>
            <h3 className="text-lg font-extrabold uppercase italic tracking-tight text-white/60 text-center">
                {title}
            </h3>
            {description && (
                <p className="text-white/30 text-sm font-inter text-center max-w-[240px]">
                    {description}
                </p>
            )}
            {action}
        </div>
    );
}
